import React, { useState, useEffect } from 'react';
import { Calendar, Filter, X, ChevronDown } from 'lucide-react';

export default function TechnologyFilters({
  onFilterChange,
  availableSprints = [],
  showSprintFilter = false,
  initialFilters = {},
}) {
  const [startDate, setStartDate] = useState(initialFilters.startDate || '');
  const [endDate, setEndDate] = useState(initialFilters.endDate || '');
  const [selectedSprints, setSelectedSprints] = useState(initialFilters.sprintIds || []);
  const [showSprintDropdown, setShowSprintDropdown] = useState(false);

  // Quick date range presets
  const datePresets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'This month', type: 'thisMonth' },
    { label: 'Last month', type: 'lastMonth' },
    { label: 'This quarter', type: 'thisQuarter' },
  ];

  const applyPreset = (preset) => {
    const today = new Date();
    let start, end;

    if (preset.days) {
      end = today.toISOString().split('T')[0];
      start = new Date(today.setDate(today.getDate() - preset.days)).toISOString().split('T')[0];
    } else if (preset.type === 'thisMonth') {
      start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      end = new Date().toISOString().split('T')[0];
    } else if (preset.type === 'lastMonth') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
      end = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
    } else if (preset.type === 'thisQuarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1).toISOString().split('T')[0];
      end = new Date().toISOString().split('T')[0];
    }

    setStartDate(start);
    setEndDate(end);
  };

  const handleSprintToggle = (sprintId) => {
    setSelectedSprints((prev) =>
      prev.includes(sprintId)
        ? prev.filter((id) => id !== sprintId)
        : [...prev, sprintId]
    );
  };

  const handleApplyFilters = () => {
    onFilterChange({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      sprintIds: selectedSprints.length > 0 ? selectedSprints : undefined,
    });
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedSprints([]);
    onFilterChange({});
  };

  const hasActiveFilters = startDate || endDate || selectedSprints.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-5 h-5 text-gray-500" />
        <h3 className="font-semibold text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="ml-auto text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
          >
            <X className="w-4 h-4" />
            Clear all
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>

        {/* Sprint Filter */}
        {showSprintFilter && availableSprints.length > 0 && (
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sprints
            </label>
            <button
              onClick={() => setShowSprintDropdown(!showSprintDropdown)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left flex items-center justify-between hover:border-gray-400"
            >
              <span className="text-gray-700">
                {selectedSprints.length === 0
                  ? 'All sprints'
                  : `${selectedSprints.length} selected`}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {showSprintDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {availableSprints.map((sprint) => (
                  <label
                    key={sprint.id}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSprints.includes(sprint.id)}
                      onChange={() => handleSprintToggle(sprint.id)}
                      className="mr-2 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">{sprint.name}</span>
                    <span
                      className={`ml-auto text-xs px-2 py-0.5 rounded ${
                        sprint.state === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {sprint.state}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Apply Button */}
        <div className="flex items-end">
          <button
            onClick={handleApplyFilters}
            className="w-full px-4 py-2 bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Quick Presets */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 mb-2">Quick presets:</p>
        <div className="flex flex-wrap gap-2">
          {datePresets.map((preset) => (
            <button
              key={preset.label}
              onClick={() => applyPreset(preset)}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
